import { useCallback, useEffect, useRef } from 'react';

/**
 * Race/unmount-safe async load. Returns `run(loader)`: each call gets a fresh
 * fetch id, and `loader` receives an `isStale()` check to call after every
 * `await` — true once a newer `run()` call superseded this one or the
 * component unmounted, so the caller can bail before touching state.
 */
export function useAbortableLoad() {
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(async (loader) => {
    const fetchId = ++fetchIdRef.current;
    const isStale = () => !mountedRef.current || fetchId !== fetchIdRef.current;
    await loader(isStale);
  }, []);
}
